import { useEffect, useState, useCallback, useRef } from 'react'
import Router, { useRouter } from 'next/router'
import { Cross } from '@components/icons'
import s from './PopUpNewsFeatures.module.scss'
import { useCmsBlocks } from '@pylot-data/hooks/cms/use-cms-blocks'
import { useTranslation } from 'react-i18next'

const MODAL_MAX_WIDTH = 320
const MODAL_ESTIMATED_HEIGHT = 300
const MARGIN = 16
const SPOT_PADDING = 6

type Spotlight = {
    x: number
    y: number
    w: number
    h: number
    r: number
}

const PopUpNewsFeatures = () => {
    const router = useRouter()
    const { t } = useTranslation('common')
    const rafRef = useRef<number | null>(null)
    const observersRef = useRef<(() => void)[]>([])
    const spotlightTargetRef = useRef<HTMLElement | null>(null)

    const [isOpen, setIsOpen] = useState(false)
    const [modalStyle, setModalStyle] = useState<React.CSSProperties>({})
    const [spot, setSpot] = useState<Spotlight>({
        x: 0,
        y: 0,
        w: 0,
        h: 0,
        r: 24
    })

    const { items: PopUp } = useCmsBlocks(['pop-up-new-features'], {
        revalidateOnMount: true,
        revalidateOnFocus: false
    })

    /* ------------------------- CMS CONTRACT ------------------------- */

    const rawTitle = PopUp?.[0]?.title ?? ''
    const [pagesRaw, spotlightClass, urlViewMore] = rawTitle.split(' ')
    const allowedPages = pagesRaw?.split('|') ?? []

    const SPOTLIGHT_SELECTOR = spotlightClass ? `.${spotlightClass}` : null

    const currentPage = (() => {
        if (router.pathname === '/') return 'HOME'
        if (router.pathname.includes('/producto')) return 'PDP'
        if (router.pathname.includes('/categoria')) return 'PLP'
        if (router.pathname.includes('/contenido')) return 'CLP'
        if (router.pathname.includes('/checkout')) return 'CHECKOUT'
        if (router.pathname.includes('/account')) return 'ACCOUNT'
        return null
    })()

    const isCorrectPage =
        currentPage !== null && allowedPages.includes(currentPage)

    /* ----------------------------- Helpers ----------------------------- */

    const waitForElement = (selector: string, timeout = 10000) =>
        new Promise<HTMLElement | null>((resolve) => {
            const start = performance.now()
            const check = () => {
                const el = document.querySelector(selector) as HTMLElement
                if (el) return resolve(el)
                if (performance.now() - start > timeout) return resolve(null)
                requestAnimationFrame(check)
            }
            check()
        })

    const centerIfNeeded = (el: HTMLElement) => {
        const rect = el.getBoundingClientRect()
        const vh = window.innerHeight
        if (rect.top < vh * 0.2 || rect.bottom > vh * 0.8) {
            el.scrollIntoView({ block: 'center' })
        }
    }

    /* ---------------------- CÁLCULO POSICIÓN ---------------------- */

    const recalcPosition = useCallback(
        (shouldCenter = false) => {
            const target =
                spotlightTargetRef.current ??
                (SPOTLIGHT_SELECTOR
                    ? (document.querySelector(
                          SPOTLIGHT_SELECTOR
                      ) as HTMLElement | null)
                    : null)

            if (!target) return

            if (shouldCenter) {
                centerIfNeeded(target)
            }

            const rect = target.getBoundingClientRect()

            setSpot({
                x: rect.left - SPOT_PADDING,
                y: rect.top - SPOT_PADDING,
                w: rect.width + SPOT_PADDING * 2,
                h: rect.height + SPOT_PADDING * 2,
                r: 24
            })

            let top = rect.bottom + MARGIN
            let left = rect.left + rect.width / 2 - MODAL_MAX_WIDTH / 2

            left = Math.max(
                MARGIN,
                Math.min(left, window.innerWidth - MODAL_MAX_WIDTH - MARGIN)
            )

            if (top + MODAL_ESTIMATED_HEIGHT > window.innerHeight) {
                top = rect.top - MODAL_ESTIMATED_HEIGHT - MARGIN
            }

            setModalStyle({
                position: 'fixed',
                top,
                left,
                width: '100%',
                maxWidth: MODAL_MAX_WIDTH
            })
        },
        [SPOTLIGHT_SELECTOR]
    )

    /* ------------------- OBSERVADORES DE CAMBIOS ------------------- */

    const stopObservers = useCallback(() => {
        observersRef.current.forEach((fn) => fn())
        observersRef.current = []
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current)
            rafRef.current = null
        }
    }, [])

    const startObservers = useCallback(
        (target: HTMLElement) => {
            spotlightTargetRef.current = target

            const throttledRecalc = () => {
                if (rafRef.current) return
                rafRef.current = requestAnimationFrame(() => {
                    rafRef.current = null
                    recalcPosition(false)
                })
            }

            const resizeObserver = new ResizeObserver(throttledRecalc)
            resizeObserver.observe(target)

            window.addEventListener('resize', throttledRecalc)
            window.addEventListener('scroll', throttledRecalc, true)

            observersRef.current = [
                () => resizeObserver.disconnect(),
                () => window.removeEventListener('resize', throttledRecalc),
                () =>
                    window.removeEventListener('scroll', throttledRecalc, true)
            ]
        },
        [recalcPosition]
    )

    /* --------------------------- Apertura --------------------------- */

    useEffect(() => {
        if (!SPOTLIGHT_SELECTOR || !isCorrectPage) return
        if (localStorage.getItem(rawTitle)) return

        let cancelled = false

        const run = async () => {
            const el = await waitForElement(SPOTLIGHT_SELECTOR)
            if (!el || cancelled) return

            if (SPOTLIGHT_SELECTOR.includes('MobileMegaMenu')) {
                await waitForElement('activeMobile')
            }

            el.scrollIntoView({ block: 'center' })

            recalcPosition(true)
            startObservers(el)

            setIsOpen(true)
            localStorage.setItem(rawTitle, 'true')
        }

        run()
        return () => {
            cancelled = true
        }
    }, [
        SPOTLIGHT_SELECTOR,
        isCorrectPage,
        rawTitle,
        recalcPosition,
        startObservers
    ])

    /* -------------------- SCROLL LOCK SIN LAYOUT -------------------- */

    useEffect(() => {
        if (!isOpen) return

        const prevent = (e: Event) => e.preventDefault()

        const preventKeys = (e: KeyboardEvent) => {
            const keys = [
                'ArrowUp',
                'ArrowDown',
                'ArrowLeft',
                'ArrowRight',
                ' ',
                'PageUp',
                'PageDown',
                'Home',
                'End'
            ]
            if (keys.includes(e.key)) e.preventDefault()
        }

        window.addEventListener('wheel', prevent, { passive: false })
        window.addEventListener('touchmove', prevent, { passive: false })
        window.addEventListener('keydown', preventKeys)

        return () => {
            window.removeEventListener('wheel', prevent)
            window.removeEventListener('touchmove', prevent)
            window.removeEventListener('keydown', preventKeys)
        }
    }, [isOpen])

    /* --------------------------- Cierre --------------------------- */

    const close = useCallback(() => {
        stopObservers()
        spotlightTargetRef.current = null
        setIsOpen(false)
    }, [stopObservers])

    const handleOverlayClick = (
        event: React.MouseEvent<HTMLDivElement, MouseEvent>
    ) => {
        if (event.target === event.currentTarget) {
            close()
        }
    }

    const handleOverlayKeyDown = (
        event: React.KeyboardEvent<HTMLDivElement>
    ) => {
        if (
            event.target === event.currentTarget &&
            ['Enter', ' ', 'Escape'].includes(event.key)
        ) {
            event.preventDefault()
            close()
        }
    }

    useEffect(() => {
        Router.events.on('routeChangeStart', close)
        Router.events.on('hashChangeStart', close)
        return () => {
            Router.events.off('routeChangeStart', close)
            Router.events.off('hashChangeStart', close)
        }
    }, [close])

    if (!isOpen) return null

    return (
        <div
            className={s.overlay}
            onClick={handleOverlayClick}
            onKeyDown={handleOverlayKeyDown}
            role="button"
            tabIndex={0}
        >
            <svg className={s.mask}>
                <defs>
                    <mask id="spotlight-mask">
                        <rect width="100%" height="100%" fill="white" />
                        <rect
                            x={spot.x}
                            y={spot.y}
                            width={spot.w}
                            height={spot.h}
                            rx={spot.r}
                            ry={spot.r}
                            fill="black"
                        />
                    </mask>
                </defs>
                <rect
                    width="100%"
                    height="100%"
                    fill="rgba(0,0,0,0.28)"
                    mask="url(#spotlight-mask)"
                />
            </svg>

            {PopUp?.[0]?.content && (
                <div className={s.modal} style={modalStyle}>
                    <button className={s.close} onClick={close}>
                        <Cross />
                    </button>

                    <div
                        className={s.content}
                        dangerouslySetInnerHTML={{
                            __html: PopUp?.[0]?.content
                        }}
                    />

                    <button className={s.confirm} onClick={close}>
                        {t('Entendido')}
                    </button>

                    {urlViewMore && (
                        <a className={s.urlViewMore} href={urlViewMore}>
                            {t('Más info')}
                        </a>
                    )}
                </div>
            )}
        </div>
    )
}

export default PopUpNewsFeatures
